import { useContext, useEffect, useRef, useState, type FunctionComponent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { identify, track, trackPageLoad } from '@fff-web/fff-analytics';
import { useUserContext } from '@fff-web/fff-utilities';
import ButtonAction from '../../interfaces/buttonAction';
import QuestionInterface from '../../interfaces/question';
import { postCancelSubscription, postQuestionnaireAnswers } from '../../services/questionnaire';
import buttonLabels from '../../utils/buttonLabels';
import { CancelReasonsProps, collectCancelReasons } from '../../utils/collectCancelReasons';
import { pageAnswers } from '../../utils/pageAnswers';
import { getPage, getQuestionIds } from '../../utils/paginator';
import { redirectToDashboard } from '../../utils/redirectMethods';
import { retrievePageErrors } from '../../utils/retrievePageErrors';
import CANCEL_TYPES from '../../utils/segment/cancelTypes';
import events from '../../utils/segment/events';
import { eventCancelFlowSkipped, eventCancelFlowStarted } from '../../utils/segment/eventsHandlers';
import CANCEL_FLOW_EXPERIENCE from '../../utils/segment/experiences';
import {
  fireCancelFlowCompleted,
  SEGMENT_EVENT,
} from '../../utils/segment/fireCancelFlowCompleted';
import { useDynamicTextContext } from '../context/DynamicTextContext';
import { MappedAnswer, SurveyPageContext } from '../context/SurveyPageContext';
import CreditModal from '../credit-modal';
import NavigationWrapper from '../navigation-wrapper/NavigationWrapper';
import Question from '../questions/Question/Question';
import * as styles from './QuestionPage.module.css';

const CANCEL_REASON_TYPE = 'Why Are You Canceling';

function trackPageSubmit(cancelReasons: CancelReasonsProps, cancelType: string) {
  track({
    trackName: events.CANCEL_REASON_SUBMITTED,
    properties: {
      cta_name: buttonLabels.CONTINUE,
      experience: CANCEL_FLOW_EXPERIENCE,
      cancel_reason_type: CANCEL_REASON_TYPE,
      cancel_reasons: cancelReasons.allReasons?.[0]?.reasons,
      cancel_type: cancelType,
    },
  });
}

interface QuestionPageProps {
  questions: QuestionInterface[];
  showCreditModal: boolean;
  closeCreditModal: () => void;
}

const QuestionPage: FunctionComponent<QuestionPageProps> = ({
  questions,
  showCreditModal,
  closeCreditModal,
}) => {
  const { dynamicText } = useDynamicTextContext();
  const { invalidateUserCache, userInfo } = useUserContext();

  const { increasePage, surveyPage, paginatedSurvey, mappedAnswers, survey } =
    useContext(SurveyPageContext);
  const mutation = useMutation({
    mutationFn: (answers: MappedAnswer[]) => postQuestionnaireAnswers(answers),
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(new Map<number, string[]>());
  const mutationStatus = mutation.status;

  const subscriptionType = userInfo?.user?.subscriptionType?.toLowerCase() as
    | 'trial'
    | 'annual'
    | 'seasonal';
  const cancelType = CANCEL_TYPES[subscriptionType] || '';

  const handleClickBack = () => {
    eventCancelFlowSkipped({
      cta_name: buttonLabels.KEEP_BOX,
      experience: CANCEL_FLOW_EXPERIENCE,
      cancel_type: cancelType,
    }).finally(() => redirectToDashboard());
  };

  const backAction = (): ButtonAction => ({
    type: 'primary',
    label: buttonLabels.KEEP_BOX,
    disabled: mutationStatus === 'error',
    onClick: handleClickBack,
  });

  const nextAction = (): ButtonAction => ({
    type: 'black-outline',
    label: isLastPage ? buttonLabels.CANCEL_BOX : buttonLabels.CONTINUE,
    disabled: loading,
    onClick: onNextButtonClick,
  });

  const currentPage = getPage({
    paginatedSurvey,
    page: surveyPage,
    defaultValue: [],
  });
  const currentPageQuestionIds = getQuestionIds({ page: currentPage });
  const isLastPage = surveyPage >= paginatedSurvey.size;
  const questionsRef = useRef<HTMLDivElement[]>([]);

  const scrollToQuestionPage = (questionId?: number) => {
    if (questionId && questionsRef.current[questionId]) {
      questionsRef.current[questionId].scrollIntoView();
    }
  };

  async function submitPagesAnswers() {
    const questionsAnswered = Array.from(mappedAnswers.values());

    // perform one mutation with all the answers of every question
    return mutation.mutateAsync(questionsAnswered);
  }

  async function cancelSubscription() {
    try {
      await postCancelSubscription();
      fireCancelFlowCompleted(SEGMENT_EVENT.success, userInfo?.user?.planCode);
      invalidateUserCache();
      identify();
    } catch {
      fireCancelFlowCompleted(SEGMENT_EVENT.failure, userInfo?.user?.planCode);
    }
  }

  async function onNextButtonClick() {
    setLoading(true);
    const currentPageAnswers = pageAnswers({
      mappedAnswers,
      currentPageQuestionIds,
    });
    const newErrors = retrievePageErrors({ currentPage, currentPageAnswers });
    setErrors(newErrors);
    if (newErrors.size > 0) {
      scrollToQuestionPage(newErrors.keys().next().value);
      setLoading(false);
      return;
    }

    try {
      const cancelReasons = collectCancelReasons({ mappedAnswers, survey });
      if (!isLastPage) {
        trackPageSubmit(cancelReasons, cancelType);
        return;
      }
      await submitPagesAnswers();
      await cancelSubscription();
    } finally {
      setLoading(false);
      increasePage();
    }
  }

  useEffect(() => {
    const hashTag = isLastPage ? '#step=confirm' : '#step=cancel';
    const pathName = `${window.location.pathname}${hashTag}`;
    dynamicText?.wooUserId && trackPageLoad({ pathName });
  }, [dynamicText?.wooUserId, isLastPage]);

  useEffect(() => {
    eventCancelFlowStarted(subscriptionType);
  }, []);

  return (
    <>
      <NavigationWrapper backButton={backAction()} nextButton={nextAction()} isQuestionPage>
        <div className={styles.questionPageQuestionsContainer}>
          {questions.map((q) => (
            <Question
              ref={(el: HTMLDivElement) => {
                questionsRef.current[q.id] = el;
              }}
              question={q}
              key={`question-${q.id}`}
              errors={errors.get(q.id) || []}
            />
          ))}
        </div>
      </NavigationWrapper>
      <CreditModal isOpen={showCreditModal} closeModal={closeCreditModal} />
    </>
  );
};

QuestionPage.displayName = 'QuestionPage';

export default QuestionPage;
